import React, { useState, useEffect } from 'react';
import { Plus, Info, Database, MessageSquare, AlertCircle } from 'lucide-react';

const CampaignCreator = ({ onCampaignCreated, onCancel }) => {
  const [formData, setFormData] = useState({
    campaign_name: '',
    empresa_id: '',
    template_id: '',
    template_name: '',
    template_language: '',
    variables_mapping: {}, // { "1": "full_name", "2": "monto" }
    send_delay_seconds: 5,
    scheduled_at: ''
  });

  const [availableVariables, setAvailableVariables] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templateError, setTemplateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  useEffect(() => {
    loadEmpresas();
    loadAvailableVariables();
    loadMetaTemplates();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingMedia(true);
    const form = new FormData();
    form.append('file', file);

    try {
      const response = await fetch('/sistemasms/backend/api/whatsapp/upload-media.php', {
        method: 'POST',
        body: form
      });
      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, media_url: 'meta_id:' + data.id }));
        alert('Imagen subida correctamente a Meta.');
      } else {
        alert('Error al subir imagen: ' + (data.message || 'Error desconocido'));
      }
    } catch (err) {
      alert('Error de conexión al subir la imagen.');
    } finally {
      setUploadingMedia(false);
    }
  };

  const loadAvailableVariables = async () => {
    try {
      const response = await fetch('/sistemasms/backend/api/campaigns/get-available-variables.php', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableVariables(data.variables || []);
      }
    } catch (error) {
      console.error('Error loading variables:', error);
    }
  };

  const loadEmpresas = async () => {
    try {
      const response = await fetch('/sistemasms/backend/api/settings/get-empresas.php', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEmpresas(data.empresas || data.data?.empresas || []);
      }
    } catch (error) {
      console.error('Error loading empresas:', error);
    }
  };

  const loadMetaTemplates = async () => {
    setLoadingTemplates(true);
    setTemplateError('');
    try {
      const response = await fetch('/sistemasms/backend/api/templates/meta-list.php', {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        setTemplates(data.templates);
      } else {
        setTemplateError(data.message || 'Error al cargar plantillas de Meta');
      }
    } catch (error) {
      setTemplateError('Error de conexión al obtener plantillas');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    const template = templates.find(t => t.id === templateId);
    
    setSelectedTemplate(template || null);
    
    setFormData(prev => ({
      ...prev,
      template_id: templateId,
      template_name: template ? template.name : '',
      template_language: template ? template.language : '',
      variables_mapping: {} // Reset variables on template change
    }));
  };

  const handleVariableMap = (variableIndex, dbField) => {
    setFormData(prev => ({
      ...prev,
      variables_mapping: {
        ...prev.variables_mapping,
        [variableIndex]: dbField
      }
    }));
  };

  const handleSubmit = async () => {
    if (!formData.campaign_name.trim()) return alert('Por favor ingresa un nombre para la campaña');
    if (!formData.empresa_id) return alert('Por favor selecciona una empresa');
    if (!formData.template_id) return alert('Debes seleccionar una plantilla de Meta');
    
    // Verificar si faltan variables por mapear
    if (selectedTemplate && selectedTemplate.variables_count > 0) {
      const mappedCount = Object.keys(formData.variables_mapping).length;
      if (mappedCount < selectedTemplate.variables_count) {
        return alert(`Faltan variables por asignar. Has asignado ${mappedCount} de ${selectedTemplate.variables_count}`);
      }
    }

    const requiresImage = selectedTemplate?.components?.some(c => c.type === 'HEADER' && c.format === 'IMAGE');
    if (requiresImage && !formData.media_url) {
      return alert("Esta plantilla requiere una URL de imagen obligatoria para el encabezado.");
    }

    setCreating(true);

    try {
      const campaignData = {
        ...formData,
        empresa_id: parseInt(formData.empresa_id),
        media_type: requiresImage ? 'image' : 'text',
        media_url: requiresImage ? formData.media_url : null,

        // Guardamos la configuración de la plantilla en message_content como JSON
        message_content: JSON.stringify({
          is_meta_template: true,
          template_name: formData.template_name,
          template_language: formData.template_language,
          variables_mapping: formData.variables_mapping
        })
      };

      const response = await fetch('/sistemasms/backend/api/campaigns/create.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(campaignData)
      });

      const result = await response.json();

      if (result.success || response.ok) {
        const newCampaign = {
          id: result.campaign_id || result.data?.campaign_id,
          ...campaignData,
          status: 'draft',
          total_recipients: result.total_recipients || 0,
          created_at: new Date().toISOString().split('T')[0]
        };

        if (onCampaignCreated) onCampaignCreated(newCampaign);
        alert(`🎉 Campaña con Plantilla Meta creada exitosamente!\n📊 Destinatarios: ${newCampaign.total_recipients}`);
      } else {
        throw new Error(result.message || 'Error desconocido');
      }
    } catch (error) {
      alert('Error creando campaña: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 mb-6 text-white shadow">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          <MessageSquare size={28} /> Crear Campaña con Meta API
        </h1>
        <p className="text-blue-100">Crea campañas masivas utilizando tus plantillas oficiales pre-aprobadas de WhatsApp.</p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la Campaña</label>
            <input
              type="text"
              value={formData.campaign_name}
              onChange={(e) => setFormData(prev => ({ ...prev, campaign_name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="Ej: Promoción Black Friday"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
            <select
              value={formData.empresa_id}
              onChange={(e) => setFormData(prev => ({ ...prev, empresa_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecciona una empresa</option>
              {empresas.map(empresa => (
                <option key={empresa.id} value={empresa.id}>{empresa.empresa_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sección Plantillas Meta */}
        <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
          <h3 className="font-medium text-gray-800 mb-4 flex items-center gap-2 text-lg">
            <MessageSquare size={20} className="text-blue-600" />
            Selección de Plantilla Meta
          </h3>

          {templateError && (
            <div className="mb-4 bg-yellow-50 p-4 border border-yellow-200 rounded-md flex items-start gap-3">
              <AlertCircle className="text-yellow-600 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-yellow-800">No se pudieron cargar las plantillas automáticamente</h4>
                <p className="text-sm text-yellow-700">{templateError}</p>
                <p className="text-xs text-yellow-600 mt-1">
                  Tu token de Meta no tiene permisos para leer plantillas, pero puedes <strong>escribir el nombre de tu plantilla manualmente</strong> a continuación.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plantillas Aprobadas</label>
            {!templateError ? (
              <select
                value={formData.template_id}
                onChange={handleTemplateSelect}
                disabled={loadingTemplates}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">
                  {loadingTemplates ? 'Cargando plantillas desde Meta...' : '-- Selecciona una plantilla --'}
                </option>
                {templates.map(template => {
                  const isApproved = template.status === 'APPROVED';
                  const statusLabel = isApproved ? 'Aprobada' : 
                                      template.status === 'PENDING' ? 'En revisión' : 
                                      template.status === 'REJECTED' ? 'Rechazada' : template.status;
                  
                  return (
                    <option key={template.id} value={template.id} disabled={!isApproved}>
                      {template.name} ({template.language}) - {isApproved ? `${template.variables_count} vars` : statusLabel}
                    </option>
                  );
                })}
              </select>
            ) : (
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={formData.template_name}
                    onChange={(e) => {
                      setFormData(prev => ({ 
                        ...prev, 
                        template_name: e.target.value,
                        template_id: 'manual_' + e.target.value 
                      }));
                      // Activar el modo manual
                      if (!selectedTemplate) {
                        setSelectedTemplate({
                          name: e.target.value,
                          language: 'es',
                          components: [{ type: 'BODY', text: 'Configuración manual de plantilla' }],
                          variables_count: 0
                        });
                      } else {
                        setSelectedTemplate(prev => ({ ...prev, name: e.target.value }));
                      }
                    }}
                    placeholder="Ej: bienvenida_clientes"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Escribe el nombre EXACTO de la plantilla tal como aparece en tu panel de Meta.</p>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Idioma</label>
                    <input
                      type="text"
                      value={formData.template_language || 'es'}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, template_language: e.target.value }));
                        setSelectedTemplate(prev => ({ ...prev, language: e.target.value }));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="es, en_US, etc."
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Cant. de Variables ({"{{1}}"}, {"{{2}}"})</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={selectedTemplate ? selectedTemplate.variables_count : 0}
                      onChange={(e) => {
                        const count = parseInt(e.target.value) || 0;
                        setSelectedTemplate(prev => ({ ...prev, variables_count: count }));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {selectedTemplate && (
            <div className="mt-4 bg-white border rounded p-4">
              <h4 className="text-sm font-bold text-gray-800 mb-2">Vista Previa de la Plantilla</h4>
              <div className="text-sm text-gray-600 bg-gray-100 p-3 rounded">
                {selectedTemplate.components.map((comp, i) => (
                  <div key={i} className="mb-2">
                    <strong className="text-xs uppercase text-gray-500">{comp.type}</strong>
                    <p className="whitespace-pre-wrap mt-1">{comp.text || (comp.type === 'HEADER' ? comp.format : '')}</p>
                  </div>
                ))}
              </div>
              
              {selectedTemplate.components?.some(c => c.type === 'HEADER' && c.format === 'IMAGE') && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-bold text-blue-800 flex items-center gap-2 mb-3">
                    <Database size={16} /> Imagen para el Encabezado
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Esta plantilla incluye una imagen en el encabezado. Puedes proporcionar una URL pública o subir un archivo desde tu equipo.
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Opción 1: Subir Archivo (JPG/PNG)</label>
                      <input 
                        type="file" 
                        accept="image/jpeg, image/png"
                        onChange={handleFileUpload}
                        disabled={uploadingMedia}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {uploadingMedia && <p className="text-xs text-blue-600 mt-1 animate-pulse">Subiendo imagen a Meta...</p>}
                    </div>
                    
                    <div className="flex items-center">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="px-3 text-xs text-gray-500 font-medium">O</span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Opción 2: Pegar URL Pública</label>
                      <input
                        type="url"
                        value={formData.media_url?.startsWith('meta_id:') ? '' : (formData.media_url || '')}
                        onChange={(e) => setFormData(prev => ({ ...prev, media_url: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="https://ejemplo.com/imagen.jpg"
                      />
                    </div>
                  </div>

                  {formData.media_url && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-xs text-green-800 font-medium mb-2">✅ Imagen configurada correctamente</p>
                      {!formData.media_url.startsWith('meta_id:') && (
                        <img src={formData.media_url} alt="Preview" className="max-h-32 rounded border border-gray-300" />
                      )}
                      {formData.media_url.startsWith('meta_id:') && (
                        <p className="text-xs text-gray-600">ID de Meta: {formData.media_url.replace('meta_id:', '')}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedTemplate.variables_count > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-bold text-blue-800 flex items-center gap-2 mb-3">
                    <Database size={16} /> Mapeo de Variables
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Esta plantilla requiere {selectedTemplate.variables_count} variables dinámicas. 
                    Selecciona qué dato de la base de datos reemplazará a cada variable.
                  </p>
                  
                  <div className="space-y-3">
                    {Array.from({ length: selectedTemplate.variables_count }).map((_, idx) => {
                      const vIndex = idx + 1;
                      return (
                        <div key={vIndex} className="flex items-center gap-3">
                          <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {`{{${vIndex}}}`}
                          </span>
                          <span className="text-gray-400">➔</span>
                          <select
                            value={formData.variables_mapping[vIndex] || ''}
                            onChange={(e) => handleVariableMap(vIndex, e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          >
                            <option value="">Seleccionar campo...</option>
                            {availableVariables.map(v => (
                              <option key={v.field} value={v.field}>{v.display_name} ({v.field})</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Delay entre mensajes (segundos)</label>
            <input
              type="number"
              value={formData.send_delay_seconds}
              onChange={(e) => setFormData(prev => ({ ...prev, send_delay_seconds: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              min="1" max="60"
            />
          </div>
        </div>

        <div className="flex gap-4 pt-6">
          <button
            onClick={handleSubmit}
            disabled={creating}
            className={`flex-1 py-3 px-6 rounded-lg font-medium text-white shadow ${
              creating ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {creating ? 'Creando...' : '🚀 Crear Campaña Meta'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignCreator;